import { Box, Skeleton, styled, Typography } from "@mui/material";
import { type FC, useState } from "react";
import { INK } from "@/theme/tokens";
import type { EditableNurse, ExperienceInDepartments, NurseFullRole, NurseProfileToSubmit } from "@/utils/models";

export const CommonHeader = styled(Typography)(({ theme }) => ({
	fontWeight: 700,
	lineHeight: "100%",
	color: INK.SECONDARY,
	[theme.breakpoints.up("xs")]: {
		fontSize: "16px",
	},
	[theme.breakpoints.up("md")]: {
		fontSize: "20px",
	},
}));

type Props = {
	lines?: number;
	avatar?: boolean;
	card?: boolean;
	actions?: boolean;
	randomLines?: boolean;
	header?: boolean;
	multipleCards?: number;
	contentDirection?: "row" | "column";
};

export const SkeletonWithRandomWidths: FC<Props> = ({
	lines = 5,
	card,
	avatar,
	actions,
	randomLines,
	header,
	multipleCards = 1,
	contentDirection = "column",
}) => {
	const [randomWidths] = useState(() => Array.from({ length: lines }, () => Math.floor(Math.random() * 50) + 50));

	return (
		<Box
			sx={{
				width: "100%",
				height: "100%",
				display: "flex",
				flexDirection: contentDirection,
				gap: 2,
			}}
		>
			{header && <Skeleton variant="text" width="40%" height={32} />}
			{avatar && (
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						gap: 2,
					}}
				>
					<Skeleton variant="circular" width={40} height={40} />
					<Skeleton variant="text" width="30%" height={24} />
				</Box>
			)}
			{randomLines &&
				randomWidths.map((randomWidth, index) => {
					return <Skeleton key={index} variant="text" width={`${randomWidth}%`} height={20} />;
				})}
			{card && (
				<Box
					sx={{
						display: "grid",
						gridTemplateColumns: `repeat(${multipleCards}, 1fr)`,
						gap: 2,
					}}
				>
					{Array.from({ length: multipleCards }).map((_, index) => (
						<Skeleton key={index} variant="rectangular" width="100%" height={80} sx={{ borderRadius: 2 }} />
					))}
				</Box>
			)}
			{actions && (
				<Box
					sx={{
						display: "flex",
						gap: 2,
					}}
				>
					<Skeleton variant="rectangular" width={80} height={36} />
					<Skeleton variant="rectangular" width={80} height={36} />
				</Box>
			)}
		</Box>
	);
};

export const isUnionMemberInEditState = (union: string | null | undefined): boolean => union != null;

export const getSanitizedNurseData = (nurse: EditableNurse | undefined) => {
	if (!nurse) return nurse;
	const nurseDataToSubmit: Omit<NurseProfileToSubmit, "role"> & { role?: NurseFullRole } = {
		...nurse,
		nursingStartingDate: nurse.nursingStartingDate?.toFormat("yyyy-MM-dd"),
		dateOfBirth: nurse.dateOfBirth?.toFormat("yyyy-MM-dd"),
		scheduledDeactivationDate: nurse.scheduledDeactivationDate?.toFormat("yyyy-MM-dd"),
		phoneNumber: nurse.phoneNumber?.trim() ?? "",
		email: nurse.email?.trim() ?? "",
		wage: nurse.wage && nurse.wage >= 0 ? nurse.wage : 0,
		firstName: nurse.firstName.trim(),
		lastName: nurse.lastName.trim(),

		/**
		 * If the user activated the union field but left it empty, we send "Union" as default value - sometimes they dont have the union name but need the indication.
		 */
		union: nurse.union != null ? (nurse.union.trim().length > 0 ? nurse.union.trim() : "Union") : null,
	};
	return nurseDataToSubmit;
};
